import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patternsAPI, tireBrandsAPI, tireTypesAPI, tagsAPI } from '../../services/garageApi';
import { buildAssetUrl } from '../../utils/helpers';
import { X, ChevronDown, Search } from 'lucide-react';
import BackButton from '../common/BackButton';

const TireTypeSelector = ({ tireTypes, selectedTireTypeIds, onTireTypesChange, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter tire types based on search term
  const filteredTireTypes = tireTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected tire types
  const selectedTireTypes = tireTypes.filter(type => selectedTireTypeIds.includes(type.id));

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleTireTypeToggle = (typeId) => {
    const newSelectedIds = selectedTireTypeIds.includes(typeId)
      ? selectedTireTypeIds.filter(id => id !== typeId)
      : [...selectedTireTypeIds, typeId];

    onTireTypesChange(newSelectedIds);
  };

  const removeTireType = (typeId) => {
    const newSelectedIds = selectedTireTypeIds.filter(id => id !== typeId);
    onTireTypesChange(newSelectedIds);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Tire Types <span className="text-red-500">*</span>
      </label>

      {/* Selected tire types as badges */}
      {selectedTireTypes.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTireTypes.map(type => (
            <span
              key={type.id}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {type.name}
              <button
                type="button"
                onClick={() => removeTireType(type.id)}
                className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none focus:bg-blue-200 focus:text-blue-600"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      <div
        className="relative w-full cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="mt-1 relative w-full border border-gray-300 bg-white rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
          <span className="block truncate text-gray-500">
            {selectedTireTypes.length === 0
              ? 'Select tire types...'
              : `${selectedTireTypes.length} type${selectedTireTypes.length !== 1 ? 's' : ''} selected`
            }
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown
              size={16}
              className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
            />
          </span>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {/* Search input */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-3 py-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search tire types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Tire type options */}
          <div className="max-h-40 overflow-auto">
            {filteredTireTypes.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? 'No tire types found' : 'No tire types available'}
              </div>
            ) : (
              filteredTireTypes.map(type => (
                <div
                  key={type.id}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 ${
                    selectedTireTypeIds.includes(type.id) ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                  }`}
                  onClick={() => handleTireTypeToggle(type.id)}
                >
                  <span className="block truncate font-normal">
                    {type.name}
                  </span>
                  {selectedTireTypeIds.includes(type.id) && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TagSelector = ({ tags, selectedTagIds, onTagsChange, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter tags based on search term
  const filteredTags = tags.filter(tag =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected tags
  const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id));

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleTagToggle = (tagId) => {
    const newSelectedIds = selectedTagIds.includes(tagId)
        ? selectedTagIds.filter(id => id !== tagId)
        : [...selectedTagIds, tagId];

    onTagsChange(newSelectedIds);
  };

  const removeTag = (tagId) => {
    const newSelectedIds = selectedTagIds.filter(id => id !== tagId);
    onTagsChange(newSelectedIds);
  };

  return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>

        {/* Selected tags as badges */}
        {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedTags.map(tag => (
                  <span
                      key={tag.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
              {tag.name}
                    <button
                        type="button"
                        onClick={() => removeTag(tag.id)}
                        className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none focus:bg-blue-200 focus:text-blue-600"
                    >
                <X size={10} />
              </button>
            </span>
              ))}
            </div>
        )}

        {/* Dropdown trigger */}
        <div
            className="relative w-full cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
        >
          <div className="mt-1 relative w-full border border-gray-300 bg-white rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
          <span className="block truncate text-gray-500">
            {selectedTags.length === 0
                ? 'Select tags...'
                : `${selectedTags.length} tag${selectedTags.length !== 1 ? 's' : ''} selected`
            }
          </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
            />
          </span>
          </div>
        </div>

        {/* Dropdown menu */}
        {isOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
              {/* Search input */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-3 py-2">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                      ref={inputRef}
                      type="text"
                      className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search tags..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              {/* Tag options */}
              <div className="max-h-40 overflow-auto">
                {filteredTags.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      {searchTerm ? 'No tags found' : 'No tags available'}
                    </div>
                ) : (
                    filteredTags.map(tag => (
                        <div
                            key={tag.id}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 ${
                                selectedTagIds.includes(tag.id) ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                            }`}
                            onClick={() => handleTagToggle(tag.id)}
                        >
                  <span className="block truncate font-normal">
                    {tag.name}
                  </span>
                          {selectedTagIds.includes(tag.id) && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                          )}
                        </div>
                    ))
                )}
              </div>
            </div>
        )}
      </div>
  );
};

const PatternForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Debug logging
  console.log('PatternForm loaded - ID:', id, 'IsEdit:', isEdit);

  const [pattern, setPattern] = useState({
    name: '',
    description: '',
    tire_brand_id: '',
    tire_type_ids: [], // Changed from tire_type_id to tire_type_ids (array)
    tag_ids: [],
    image: null, // File object for image upload
    is_active: true
  });

  const [tireBrands, setTireBrands] = useState([]);
  const [tireTypes, setTireTypes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [brandsRes, typesRes, tagsRes] = await Promise.all([
          tireBrandsAPI.getAll(),
          tireTypesAPI.getAll(),
          tagsAPI.getAll()
        ]);

        console.log('Dropdown data loaded:', {
          brands: brandsRes.data,
          types: typesRes.data,
          tags: tagsRes.data
        });

        setTireBrands(brandsRes.data.data || brandsRes.data || []);
        setTireTypes(typesRes.data.data || typesRes.data || []);
        setTags(tagsRes.data.data || tagsRes.data || []);
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
        setError('Failed to load form data');
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch pattern data if editing
  const fetchPattern = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching pattern with ID:', id);

      const res = await patternsAPI.show(id);
      console.log('Pattern response:', res);

      // patternsAPI.show already extracts the data, so res.data is the pattern object
      const patternData = res.data || {};
      console.log('Pattern data:', patternData);

      // Extract tire type IDs from tire_types array
      const tireTypeIds = patternData.tire_types && Array.isArray(patternData.tire_types)
        ? patternData.tire_types.map(type => type.id)
        : [];

      // Set image preview if image exists
      if (patternData.image) {
        const imageUrl = buildAssetUrl(patternData.image);
        setImagePreview(imageUrl);
      }

      setPattern({
        name: patternData.name || '',
        description: patternData.description || '',
        tire_brand_id: patternData.tire_brand_id || patternData.tire_brand?.id || '',
        tire_type_ids: tireTypeIds, // Changed from tire_type_id to tire_type_ids array
        tag_ids: patternData.tags ? patternData.tags.map(tag => tag.id) : [],
        image: null, // Don't set existing image as file
        is_active: patternData.is_active !== undefined ? patternData.is_active : true
      });
    } catch (err) {
      console.error('Error fetching pattern:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || 'Failed to load pattern');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit && id) {
      fetchPattern();
    }
  }, [isEdit, id, fetchPattern]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPattern(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTagsChange = (tagIds) => {
    setPattern(prev => ({
      ...prev,
      tag_ids: tagIds
    }));
  };

  const handleTireTypesChange = (tireTypeIds) => {
    setPattern(prev => ({
      ...prev,
      tire_type_ids: tireTypeIds
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please select JPEG, PNG, JPG, or WEBP.');
        return;
      }

      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        return;
      }

      setPattern(prev => ({ ...prev, image: file }));
      setError(''); // Clear any previous errors

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!pattern.name.trim()) {
      setError('Pattern name is required');
      return;
    }

    if (!pattern.tire_brand_id) {
      setError('Tire brand is required');
      return;
    }

    if (!pattern.tire_type_ids || pattern.tire_type_ids.length === 0) {
      setError('At least one tire type is required');
      return;
    }

    try {
      setLoading(true);

      // Create FormData for multipart/form-data
      const formData = new FormData();

      formData.append('name', pattern.name.trim());
      
      if (pattern.description) {
        formData.append('description', pattern.description.trim());
      }

      formData.append('tire_brand_id', pattern.tire_brand_id);

      // Append each tire type ID as array
      pattern.tire_type_ids.forEach(id => {
        formData.append('tire_type_ids[]', id);
      });

      // Append each tag ID as array
      pattern.tag_ids.forEach(id => {
        formData.append('tag_ids[]', id);
      });

      // Append image if selected
      if (pattern.image && pattern.image instanceof File) {
        formData.append('image', pattern.image);
      }

      formData.append('is_active', pattern.is_active ? '1' : '0');

      console.log('Submitting FormData with:', {
        name: pattern.name,
        tire_brand_id: pattern.tire_brand_id,
        tire_type_ids: pattern.tire_type_ids,
        tag_ids: pattern.tag_ids,
        hasImage: !!pattern.image
      });

      if (isEdit) {
        await patternsAPI.update(id, formData);
      } else {
        await patternsAPI.create(formData);
      }

      navigate('/patterns');
    } catch (err) {
      console.error('Error saving pattern:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.response?.data?.errors) {
        const errorMessages = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(errorMessages);
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save pattern');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );
  }

  return (
      <div className="mx-auto max-w-2xl">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <BackButton to="/patterns" />
              <h1 className="text-2xl font-bold text-gray-800">
                {isEdit ? 'Edit Pattern' : 'Create New Pattern'}
              </h1>
            </div>
          </div>

          <div className="p-6">
            {error && (
                <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Pattern Name <span className="text-red-500">*</span>
                  </label>
                  <input
                      type="text"
                      name="name"
                      id="name"
                      value={pattern.name}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Enter pattern name"
                  />
                </div>

                <div className="col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                      name="description"
                      id="description"
                      value={pattern.description}
                      onChange={handleChange}
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Enter pattern description"
                  />
                </div>

                <div>
                  <label htmlFor="tire_brand_id" className="block text-sm font-medium text-gray-700">
                    Tire Brand <span className="text-red-500">*</span>
                  </label>
                  <select
                      name="tire_brand_id"
                      id="tire_brand_id"
                      value={pattern.tire_brand_id}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="">Select a brand</option>
                    {tireBrands.map(brand => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <TireTypeSelector
                      tireTypes={tireTypes}
                      selectedTireTypeIds={pattern.tire_type_ids}
                      onTireTypesChange={handleTireTypesChange}
                  />
                </div>

                <div className="col-span-2">
                  <TagSelector
                      tags={tags}
                      selectedTagIds={pattern.tag_ids}
                      onTagsChange={handleTagsChange}
                  />
                </div>

                <div className="col-span-2">
                  <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
                    Pattern Image
                  </label>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mb-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                      />
                    </div>
                  )}

                  {/* Image Input */}
                  <div className="relative">
                    <input
                      type="file"
                      name="image"
                      id="image"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={handleImageChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    JPEG, PNG, JPG, or WEBP. Max 5MB.
                  </p>
                  {pattern.image && (
                    <p className="mt-1 text-xs text-green-600">
                      Selected: {pattern.image.name} ({(pattern.image.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>

                <div className="col-span-2">
                  <div className="flex items-center">
                    <input
                        type="checkbox"
                        name="is_active"
                        id="is_active"
                        checked={pattern.is_active}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Active Pattern
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                    type="button"
                    onClick={() => navigate('/patterns')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                  ) : (
                      isEdit ? 'Update Pattern' : 'Create Pattern'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  );
};

export default PatternForm;